import classNames from 'classnames';
import { Button } from 'shared/ui/Button';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { Tooltip } from 'shared/ui/Tooltip';
import type { DataAttributes } from 'shared/lib/data-auto-test';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { isString, isNumber } from 'shared/lib/type-guards';
import { formatNumberToView } from 'shared/lib/format';

const TO_FIXED = 3;

interface InfoCardProps extends DataAttributes {
  children?: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'auto';
  className?: string;
  color?: string;
}

export function CommonCard({
  attributes,
  children,
  size = 'medium',
  className,
  color = 'border-deep-blue-100',
}: InfoCardProps) {
  return (
    <div
      {...attributes}
      className={classNames('p-[15px] rounded-lg border min-w-[270px]', className, color, {
        'h-[108px]': size === 'small',
        'h-[120px]': size === 'medium',
        'h-[146px]': size === 'large',
        'h-auto': size === 'auto',
      })}
    >
      <div className="flex flex-col items-start">{children}</div>
    </div>
  );
}

export type InfoTooltip = {
  content: JSX.Element | string;
  placement?: 'top-start' | 'bottom-end';
  colorClassName?: string;
  iconName?: string;
  attributes?: {
    icon: DataAttributes['attributes'];
    tooltip: DataAttributes['attributes'];
  };
};

interface LabelProps {
  label: string;
  className?: string;
  infoTooltips?: InfoTooltip[];
}

function Label(props: LabelProps) {
  const { label, infoTooltips, className } = props;

  const infoTooltipDefaultColor = 'text-slate-500 hover:text-deep-blue-500';

  return (
    <span className={classNames('flex justify-between text-sm text-slate-700 font-medium', className)}>
      {label}
      {infoTooltips?.map((infoTooltip, idx) => (
        <Tooltip
          key={idx}
          placement={infoTooltip.placement ?? 'top-start'}
          content={infoTooltip.content}
          className={classNames(
            'ml-1 flex cursor-pointer items-center justify-center',
            infoTooltip.colorClassName ?? infoTooltipDefaultColor
          )}
          attributes={infoTooltip.attributes?.tooltip}
        >
          <MaterialIcon
            className="text-[16px]"
            attributes={{ ...getDataAutoTestAttributes([`label-icon`]).attributes }}
          >
            {infoTooltip.iconName ?? 'info'}
          </MaterialIcon>
        </Tooltip>
      ))}
    </span>
  );
}

interface ValueGroupProps extends DataAttributes {
  value: string | number;
  additionalValue?: string;
}

function ValueGroup({ value, additionalValue, attributes }: ValueGroupProps) {
  const formatValue = formatNumberToView(value, TO_FIXED);
  const isAdditional = isString(additionalValue);

  return (
    <>
      <span
        {...attributes}
        className={classNames('text-slate-900 font-semibold', {
          'text-[35px]': !isAdditional,
          'text-[18px]': isAdditional,
        })}
      >
        {formatValue}
      </span>
      {isAdditional && <span className="text-gray-900 text-[14px]">{additionalValue}</span>}
    </>
  );
}

interface ValueProps extends DataAttributes {
  value: string;
  className?: string;
}

function Value({ value, className, attributes }: ValueProps) {
  return (
    <span
      {...attributes}
      className={classNames('text-gray-900 text-base font-semibold', className)}
    >
      {value}
    </span>
  );
}

function SubValue({ value, className }: ValueProps) {
  return <span className={classNames('text-gray-900 text-sm font-semibold', className)}>{value}</span>;
}

interface ActionBtnProps {
  iconName: string;
  onAction?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  isDisabled?: boolean;
  attributes?: DataAttributes['attributes'];
}

function ActionBtn({ onAction, iconName, isDisabled, attributes }: ActionBtnProps) {
  return (
    <Button
      onClick={onAction}
      disabled={isDisabled}
      contentClassName="flex items-center justify-center"
      className="ml-auto flex w-8 items-center justify-center !border-transparent bg-deep-blue-25 !px-3"
      variant="outline"
      size="small"
      type="button"
      attributes={attributes}
    >
      <MaterialIcon className="text-[16px]">{iconName}</MaterialIcon>
    </Button>
  );
}

interface CurrentPreviousNumberProps {
  current: number;
  previous?: number;
  difference?: number;
  percentage?: number;
}

function CurrentPreviousNumber({ current, previous, difference, percentage }: CurrentPreviousNumberProps) {
  const currentFixed = formatNumberToView(current, TO_FIXED);
  const previousFormat = previous && formatNumberToView(previous, TO_FIXED);
  const differenceFormat = difference && formatNumberToView(difference, TO_FIXED);

  const isDifferenceNegative = Number(difference) < 0;
  const isNoDifference = Number(difference) === 0;

  const negAtiveDifference = 'text-magenta-700 bg-magenta-50';
  const noDifference = 'bg-slate-50 text-slate-300';
  const positiveDifference = 'text-mint-700 bg-mint-50';

  return (
    <div className="w-full">
      <div className="flex min-h-[61px] items-center justify-between">
        <div className="">
          <span
            className="text-[35px] font-semibold text-slate-900"
            {...getDataAutoTestAttributes(['banner-current-value']).attributes}
          >
            {currentFixed}
          </span>
          {!!previousFormat && (
            <span className="text-[25px] font-semibold text-slate-200">
              <span className="mx-1">/</span>
              <span {...getDataAutoTestAttributes(['banner-previous-value']).attributes}>{previousFormat}</span>
            </span>
          )}
        </div>
        <div className="mt-[11px]">
          {percentage !== undefined && (
            <span
              {...getDataAutoTestAttributes(['banner-percentage-value']).attributes}
              className="text-slate-70 ml-auto h-[24px] w-[41px] rounded-sm bg-slate-50 px-2 py-1 text-xs font-normal text-slate-700"
            >
              {percentage}%
            </span>
          )}
        </div>
      </div>
      {isNumber(difference) && (
        <div className="mt-[-3px] flex gap-1">
          <span
            {...getDataAutoTestAttributes(['banner-difference-value']).attributes}
            className={classNames('rounded-lg  px-2 py-1 text-xs font-semibold', {
              [negAtiveDifference]: isDifferenceNegative,
              [positiveDifference]: !isDifferenceNegative && !isNoDifference,
              [noDifference]: isNoDifference,
            })}
          >
            {!isDifferenceNegative && !isNoDifference && '+'}
            {differenceFormat}
          </span>
          <span
            className="flex items-center text-xs font-medium text-slate-600"
            {...getDataAutoTestAttributes(['banner-difference-value-caption']).attributes}
          >
            {isNoDifference ? 'no updates since last week' : 'since last week'}
          </span>
        </div>
      )}
    </div>
  );
}

function CardTopBlock({ children }: { children: React.ReactNode }) {
  return <div className="flex w-full items-center justify-between">{children}</div>;
}

CommonCard.Label = Label;
CommonCard.Value = Value;
CommonCard.ValueGroup = ValueGroup;
CommonCard.SubValue = SubValue;
CommonCard.ActionBtn = ActionBtn;
CommonCard.TopBlock = CardTopBlock;
CommonCard.CurrentPreviousNumber = CurrentPreviousNumber;
